import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import './human-task-user-info.scss';

interface Fact {
  factType: string;
  value: string | Record<string, unknown>;
}

interface OperatorContextItem {
  category: 'preference' | 'background' | 'relationship' | 'sensitivity';
  content: string;
}

interface UserInfoData {
  userName: string | null;
  userEmail: string | null;
  agentName: string | null;
  agentEmail: string | null;
  taskStatus: string;
  createdAt: string | null;
  acceptedAt: string | null;
  workerName: string | null;
  userFacts: Fact[];
  companyFacts: Fact[];
  operatorContext: OperatorContextItem[] | null;
}

function formatFactValue(value: string | Record<string, unknown>): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatElapsed(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}h ${mins}m ${secs}s`;
  }
  return `${mins}m ${secs}s`;
}

/** Hook that returns a live elapsed-seconds counter from a given start time */
function useElapsedTimer(startIso: string | null): number | null {
  const [elapsed, setElapsed] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!startIso) {
      setElapsed(null);
      return;
    }

    const startMs = new Date(startIso).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - startMs) / 1000));

    tick(); // immediate first value
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startIso]);

  return elapsed;
}

interface HumanTaskUserInfoProps {
  taskId: string;
}

const HumanTaskUserInfo: React.FC<HumanTaskUserInfoProps> = ({ taskId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<UserInfoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchUserInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getAccessTokenSilently();
        const res = await api.get(`/human-tasks/${taskId}/user-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data.success) {
          setData(res.data);
        } else if (!cancelled) {
          setError('Failed to load user info');
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const axiosErr = err as { response?: { data?: { error?: string } } };
          setError(
            axiosErr?.response?.data?.error || 'Failed to load user info',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUserInfo();
    return () => {
      cancelled = true;
    };
  }, [taskId, getAccessTokenSilently]);

  const elapsedSeconds = useElapsedTimer(data?.acceptedAt ?? null);

  if (loading) {
    return (
      <div className='human-task-user-info loading'>
        <p>Loading user info...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className='human-task-user-info error'>
        <p>{error || 'User info unavailable'}</p>
      </div>
    );
  }

  const operatorContext: OperatorContextItem[] = data.operatorContext || [];

  return (
    <div className='human-task-user-info'>
      <div
        className={`info-layout${operatorContext.length > 0 ? ' has-context' : ''}`}
      >
        <div className='info-main'>
          {/* User Information */}
          <div className='info-section'>
            <h4 className='section-title'>User Information</h4>
            <div className='info-grid'>
              <div className='info-row'>
                <span className='info-label'>Name</span>
                <span className='info-value'>{data.userName || '—'}</span>
              </div>
              <div className='info-row'>
                <span className='info-label'>Email</span>
                <span className='info-value'>{data.userEmail || '—'}</span>
              </div>
              {data.userFacts
                .filter((f) => f.factType === 'user.phone')
                .map((f) => (
                  <div key={f.factType} className='info-row'>
                    <span className='info-label'>Phone</span>
                    <span className='info-value'>
                      {formatFactValue(f.value)}
                    </span>
                  </div>
                ))}
              {data.userFacts
                .filter((f) => f.factType === 'user.location')
                .map((f) => (
                  <div key={f.factType} className='info-row'>
                    <span className='info-label'>Home Address</span>
                    <span className='info-value'>
                      {formatFactValue(f.value)}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* PA Information */}
          {(data.agentName || data.agentEmail) && (
            <div className='info-section'>
              <h4 className='section-title'>Secretary Information</h4>
              <div className='info-grid'>
                {data.agentName && (
                  <div className='info-row'>
                    <span className='info-label'>Name</span>
                    <span className='info-value'>{data.agentName}</span>
                  </div>
                )}
                {data.agentEmail && (
                  <div className='info-row'>
                    <span className='info-label'>Email</span>
                    <span className='info-value'>{data.agentEmail}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Task Data */}
          <div className='info-section'>
            <h4 className='section-title'>Task Data</h4>
            <div className='info-grid'>
              <div className='info-row'>
                <span className='info-label'>Status</span>
                <span className='info-value status-badge'>
                  {data.taskStatus}
                </span>
              </div>
              <div className='info-row'>
                <span className='info-label'>Created</span>
                <span className='info-value'>
                  {formatDateTime(data.createdAt)}
                </span>
              </div>
              <div className='info-row'>
                <span className='info-label'>Accepted</span>
                <span className='info-value'>
                  {formatDateTime(data.acceptedAt)}
                </span>
              </div>
              {data.workerName && (
                <div className='info-row'>
                  <span className='info-label'>Worker</span>
                  <span className='info-value'>{data.workerName}</span>
                </div>
              )}
              <div className='info-row'>
                <span className='info-label'>Time Elapsed</span>
                <span className='info-value elapsed-timer'>
                  {elapsedSeconds !== null
                    ? formatElapsed(elapsedSeconds)
                    : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Operator Context — right sidebar */}
        {operatorContext.length > 0 && (
          <div className='info-sidebar'>
            <div className='operator-context-section'>
              <h4 className='section-title'>Key Context</h4>
              <div className='operator-context-list'>
                {operatorContext.map((item, idx) => (
                  <div
                    key={idx}
                    className={`context-item context-${item.category}`}
                  >
                    <span className='context-category'>
                      {CATEGORY_LABELS[item.category]}
                    </span>
                    <span className='context-content'>{item.content}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CATEGORY_LABELS: Record<OperatorContextItem['category'], string> = {
  preference: 'Preference',
  background: 'Background',
  relationship: 'Relationship',
  sensitivity: 'Sensitivity',
};

export default HumanTaskUserInfo;
